/**
 * ============================================================================
 *  File        : frontend/src/pages/ReportsPage.tsx
 *  Module      : pages
 *  Purpose     : /reports 報表中心（spec §8.2）— 6 份報表 Tabs + 共用篩選 + CSV 匯出
 *  Author      : EF Engineering
 *  Created     : 2026-06-02
 *  Updated     : 2026-06-02
 *  License     : Proprietary - ECO FAITH Platform v3.0
 * ============================================================================
 *
 *  6 份報表（各為一個 shadcn Tab + TanStack Query + shadcn Table）：
 *    案件進度 / 重量差異 / 精算 / 入札 / Freee SLA / Audit。
 *  共用篩選列（from / to / status / case_no）套用到「目前作用中的 tab」;分頁讀 meta.total。
 *  每個 tab 一顆「匯出 CSV」(data-testid="report-export-csv"):client-side Blob,把
 *  巢狀物件（精算 p1/p2/p3、入札 awarded）合理攤平。
 *  RBAC：頁面已由路由 gate（marketplace.settlements.view = reportRead);Audit tab
 *  另需 system.audit.view（auditRead),不足則隱藏該 tab。
 */

import { useState, type ReactElement, type ReactNode } from 'react'
import { keepPreviousData, useQuery, type UseQueryResult } from '@tanstack/react-query'
import { ApiError, type Envelope } from '../api/client'
import {
  getAuctionsReport,
  getAuditReport,
  getCasesProgressReport,
  getFreeeSlaReport,
  getSettlementsReport,
  getWeightReconciliationReport,
  type AuctionSummaryRow,
  type AuditRow,
  type CaseProgressRow,
  type FreeeSlaRow,
  type ReportQuery,
  type SettlementPhaseSummary,
  type SettlementSummaryRow,
  type WeightReconciliationRow,
} from '../api/reportsHub'
import { usePermissions } from '../hooks/usePermissions'
import { PERMISSIONS } from '../lib/permissions'
import { exportRowsToCsv, type CsvColumn } from '../lib/csv'
import { Badge } from '../components/ui/badge'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { Skeleton } from '../components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table'

interface PageProps {
  t: (key: string) => string
}

type Translate = (key: string) => string

const PER_PAGE = 20

/** 套用後的篩選條件（送進 query;空字串視為未設）。 */
interface AppliedFilters {
  from?: string
  to?: string
  status?: string
  caseNo?: string
}

/**
 * 單一報表欄位定義：表格標頭、表格 cell render、CSV 取值。
 * `cell` 給表格用（可回 ReactNode,如 Badge);`csv` 給匯出用（純字串/數字）。
 * `csv` 省略時退回以 `cell` 不適用 → 各 tab 一律明確提供 csv。
 */
interface ReportColumn<TRow> {
  /** i18n key（標頭文字）。 */
  headerKey: string
  /** 表格 cell 內容。 */
  cell: (row: TRow) => ReactNode
  /** CSV 值（攤平後的純量）。 */
  csv: (row: TRow) => string | number | null | undefined
}

/** 把 datetime 字串顯示為本地可讀格式;無值回 —。 */
function fmtDateTime(value: string | null | undefined): string {
  if (!value) return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value
  return d.toLocaleString()
}

/** 純量顯示 helper：null/undefined → —。 */
function dash(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—'
  return String(value)
}

/** string[] 摘要顯示：join(", ");空回 —。 */
function summaryText(value: string[] | null | undefined): string {
  if (!value || value.length === 0) return '—'
  return value.join(', ')
}

export function ReportsPage({ t }: PageProps): ReactElement {
  const { can } = usePermissions()
  const canAudit = can(PERMISSIONS.auditRead)

  // 草稿（輸入中）與已套用篩選分離：按「套用」才打 query（與 CasesListPage 慣例一致）。
  const [draft, setDraft] = useState<AppliedFilters>({})
  const [applied, setApplied] = useState<AppliedFilters>({})

  const applyFilters = (): void => {
    setApplied({
      from: draft.from?.trim() || undefined,
      to: draft.to?.trim() || undefined,
      status: draft.status?.trim() || undefined,
      caseNo: draft.caseNo?.trim() || undefined,
    })
  }

  const resetFilters = (): void => {
    setDraft({})
    setApplied({})
  }

  return (
    <section className="page-stack">
      <header className="page-heading">
        <span className="eyebrow">{t('nav.reports')}</span>
        <h1>{t('reports.title')}</h1>
        <p>{t('reports.subtitle')}</p>
      </header>

      <FilterBar t={t} draft={draft} setDraft={setDraft} onApply={applyFilters} onReset={resetFilters} />

      <Tabs defaultValue="cases-progress">
        <TabsList className="flex-wrap">
          <TabsTrigger value="cases-progress" data-testid="report-tab-cases-progress">
            {t('reports.tab.casesProgress')}
          </TabsTrigger>
          <TabsTrigger value="weight" data-testid="report-tab-weight">
            {t('reports.tab.weight')}
          </TabsTrigger>
          <TabsTrigger value="settlements" data-testid="report-tab-settlements">
            {t('reports.tab.settlements')}
          </TabsTrigger>
          <TabsTrigger value="auctions" data-testid="report-tab-auctions">
            {t('reports.tab.auctions')}
          </TabsTrigger>
          <TabsTrigger value="freee-sla" data-testid="report-tab-freee-sla">
            {t('reports.tab.freeeSla')}
          </TabsTrigger>
          {canAudit ? (
            <TabsTrigger value="audit" data-testid="report-tab-audit">
              {t('reports.tab.audit')}
            </TabsTrigger>
          ) : null}
        </TabsList>

        <TabsContent value="cases-progress">
          <ReportTab
            t={t}
            filters={applied}
            queryKey="cases-progress"
            fetcher={getCasesProgressReport}
            columns={CASES_PROGRESS_COLUMNS}
            csvName="cases-progress"
          />
        </TabsContent>
        <TabsContent value="weight">
          <ReportTab
            t={t}
            filters={applied}
            queryKey="weight-reconciliation"
            fetcher={getWeightReconciliationReport}
            columns={WEIGHT_COLUMNS}
            csvName="weight-reconciliation"
          />
        </TabsContent>
        <TabsContent value="settlements">
          <ReportTab
            t={t}
            filters={applied}
            queryKey="settlements"
            fetcher={getSettlementsReport}
            columns={SETTLEMENT_COLUMNS}
            csvName="settlements"
          />
        </TabsContent>
        <TabsContent value="auctions">
          <ReportTab
            t={t}
            filters={applied}
            queryKey="auctions"
            fetcher={getAuctionsReport}
            columns={AUCTION_COLUMNS}
            csvName="auctions"
          />
        </TabsContent>
        <TabsContent value="freee-sla">
          <ReportTab
            t={t}
            filters={applied}
            queryKey="freee-sla"
            fetcher={getFreeeSlaReport}
            columns={FREEE_SLA_COLUMNS}
            csvName="freee-sla"
          />
        </TabsContent>
        {canAudit ? (
          <TabsContent value="audit">
            <ReportTab
              t={t}
              filters={applied}
              queryKey="audit"
              fetcher={getAuditReport}
              columns={AUDIT_COLUMNS}
              csvName="audit"
            />
          </TabsContent>
        ) : null}
      </Tabs>
    </section>
  )
}

/** 共用篩選列：from / to / status / case_no + 套用 / 清除。 */
function FilterBar({
  t,
  draft,
  setDraft,
  onApply,
  onReset,
}: {
  t: Translate
  draft: AppliedFilters
  setDraft: (next: AppliedFilters) => void
  onApply: () => void
  onReset: () => void
}): ReactElement {
  return (
    <form
      className="flex flex-wrap items-end gap-3 rounded-md border border-border p-3"
      onSubmit={(e) => {
        e.preventDefault()
        onApply()
      }}
    >
      <div className="flex flex-col gap-1">
        <Label htmlFor="report-from">{t('reports.filter.from')}</Label>
        <Input
          id="report-from"
          type="date"
          value={draft.from ?? ''}
          onChange={(e) => setDraft({ ...draft, from: e.target.value })}
          className="w-40"
        />
      </div>
      <div className="flex flex-col gap-1">
        <Label htmlFor="report-to">{t('reports.filter.to')}</Label>
        <Input
          id="report-to"
          type="date"
          value={draft.to ?? ''}
          onChange={(e) => setDraft({ ...draft, to: e.target.value })}
          className="w-40"
        />
      </div>
      <div className="flex flex-col gap-1">
        <Label htmlFor="report-status">{t('reports.filter.status')}</Label>
        <Input
          id="report-status"
          value={draft.status ?? ''}
          onChange={(e) => setDraft({ ...draft, status: e.target.value })}
          placeholder={t('reports.filter.statusPlaceholder')}
          className="w-48"
        />
      </div>
      <div className="flex flex-col gap-1">
        <Label htmlFor="report-case-no">{t('reports.filter.caseNo')}</Label>
        <Input
          id="report-case-no"
          value={draft.caseNo ?? ''}
          onChange={(e) => setDraft({ ...draft, caseNo: e.target.value })}
          placeholder={t('reports.filter.caseNoPlaceholder')}
          className="w-48"
        />
      </div>
      <div className="flex gap-2">
        <Button type="submit">{t('reports.filter.apply')}</Button>
        <Button type="button" variant="outline" onClick={onReset}>
          {t('reports.filter.reset')}
        </Button>
      </div>
    </form>
  )
}

/**
 * 單一報表 tab：依 fetcher + columns 跑 TanStack Query → Table，含分頁與 CSV 匯出。
 * filters 來自頁面共用篩選;page 為 tab 自身分頁狀態（切篩選時重置回 1）。
 */
function ReportTab<TRow>({
  t,
  filters,
  queryKey,
  fetcher,
  columns,
  csvName,
}: {
  t: Translate
  filters: AppliedFilters
  queryKey: string
  fetcher: (query: ReportQuery) => Promise<Envelope<TRow[]>>
  columns: readonly ReportColumn<TRow>[]
  csvName: string
}): ReactElement {
  const [page, setPage] = useState(1)

  // 篩選變更時重置回第 1 頁（filters 物件每次套用都是新 reference）。
  const filtersKey = JSON.stringify(filters)
  const [lastFiltersKey, setLastFiltersKey] = useState(filtersKey)
  if (filtersKey !== lastFiltersKey) {
    setLastFiltersKey(filtersKey)
    setPage(1)
  }

  const query: UseQueryResult<Envelope<TRow[]>, ApiError> = useQuery({
    queryKey: ['report', queryKey, filtersKey, page],
    queryFn: () =>
      fetcher({
        from: filters.from,
        to: filters.to,
        status: filters.status,
        caseNo: filters.caseNo,
        page,
        perPage: PER_PAGE,
      }),
    placeholderData: keepPreviousData,
  })

  const rows = query.data?.data ?? []
  const total = query.data?.meta?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE))

  const handleExport = (): void => {
    const csvColumns: CsvColumn<TRow>[] = columns.map((c) => ({
      header: t(c.headerKey),
      value: c.csv,
    }))
    exportRowsToCsv(`${csvName}.csv`, rows, csvColumns)
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-end">
        <Button
          type="button"
          variant="outline"
          size="sm"
          data-testid="report-export-csv"
          disabled={rows.length === 0}
          onClick={handleExport}
        >
          {t('reports.exportCsv')}
        </Button>
      </div>

      {query.isError ? (
        <p className="text-sm text-destructive">
          {t('reports.loadError')}
          {query.error instanceof ApiError ? `（${query.error.message}）` : ''}
        </p>
      ) : null}

      <div className="rounded-md border border-border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              {columns.map((c) => (
                <TableHead key={c.headerKey}>{t(c.headerKey)}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {query.isPending ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={`skeleton-${i}`}>
                  {columns.map((c) => (
                    <TableCell key={`cell-${i}-${c.headerKey}`}>
                      <Skeleton className="h-4 w-full" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="text-center text-muted-foreground">
                  {t('reports.empty')}
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row, idx) => (
                <TableRow key={idx}>
                  {columns.map((c) => (
                    <TableCell key={c.headerKey}>{c.cell(row)}</TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-between text-sm text-muted-foreground">
        <span>{t('reports.total').replace('{total}', String(total))}</span>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1 || query.isFetching}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            {t('reports.prev')}
          </Button>
          <span>
            {t('reports.page').replace('{page}', String(page))} / {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages || query.isFetching}
            onClick={() => setPage((p) => p + 1)}
          >
            {t('reports.next')}
          </Button>
        </div>
      </div>
    </div>
  )
}

// ---- 各報表欄位定義（標頭 i18n key + table cell + csv 攤平）-----------------

const CASES_PROGRESS_COLUMNS: readonly ReportColumn<CaseProgressRow>[] = [
  { headerKey: 'reports.col.caseNo', cell: (r) => r.case_no, csv: (r) => r.case_no },
  { headerKey: 'reports.col.title', cell: (r) => r.title, csv: (r) => r.title },
  {
    headerKey: 'reports.col.workflowState',
    cell: (r) => <Badge variant="secondary">{dash(r.workflow_state)}</Badge>,
    csv: (r) => r.workflow_state ?? '',
  },
  {
    headerKey: 'reports.col.memberOrg',
    cell: (r) => dash(r.member_org_id),
    csv: (r) => r.member_org_id ?? '',
  },
  {
    headerKey: 'reports.col.venueOrg',
    cell: (r) => dash(r.venue_org_id),
    csv: (r) => r.venue_org_id ?? '',
  },
  {
    headerKey: 'reports.col.openExceptions',
    cell: (r) => r.open_exceptions,
    csv: (r) => r.open_exceptions,
  },
  { headerKey: 'reports.col.nextStep', cell: (r) => dash(r.next_step), csv: (r) => r.next_step },
  {
    headerKey: 'reports.col.createdAt',
    cell: (r) => fmtDateTime(r.created_at),
    csv: (r) => r.created_at,
  },
]

const WEIGHT_COLUMNS: readonly ReportColumn<WeightReconciliationRow>[] = [
  { headerKey: 'reports.col.reviewId', cell: (r) => r.review_id, csv: (r) => r.review_id },
  { headerKey: 'reports.col.caseId', cell: (r) => r.case_id, csv: (r) => r.case_id },
  {
    headerKey: 'reports.col.itemName',
    cell: (r) => dash(r.item_name),
    csv: (r) => r.item_name ?? '',
  },
  {
    headerKey: 'reports.col.varianceType',
    cell: (r) => r.variance_type,
    csv: (r) => r.variance_type,
  },
  {
    headerKey: 'reports.col.memberWeight',
    cell: (r) => dash(r.member_weight_kg),
    csv: (r) => r.member_weight_kg ?? '',
  },
  {
    headerKey: 'reports.col.venueWeight',
    cell: (r) => dash(r.venue_weight_kg),
    csv: (r) => r.venue_weight_kg ?? '',
  },
  {
    headerKey: 'reports.col.finalWeight',
    cell: (r) => dash(r.final_weight_kg),
    csv: (r) => r.final_weight_kg ?? '',
  },
  { headerKey: 'reports.col.varianceKg', cell: (r) => r.variance_kg, csv: (r) => r.variance_kg },
  { headerKey: 'reports.col.thresholdKg', cell: (r) => r.threshold_kg, csv: (r) => r.threshold_kg },
  {
    headerKey: 'reports.col.status',
    cell: (r) => <Badge variant="outline">{r.status}</Badge>,
    csv: (r) => r.status,
  },
  {
    headerKey: 'reports.col.createdAt',
    cell: (r) => fmtDateTime(r.created_at),
    csv: (r) => r.created_at,
  },
]

/** 從 phase 摘要取出「issued/calculated」狀態文字;無 phase → —。 */
function phaseStatus(phase: SettlementPhaseSummary | null | undefined): string {
  if (!phase) return '—'
  return phase.status
}

const SETTLEMENT_COLUMNS: readonly ReportColumn<SettlementSummaryRow>[] = [
  { headerKey: 'reports.col.caseId', cell: (r) => r.case_id, csv: (r) => r.case_id },
  // P1：①L34 税込総額 / M34 50% 参考 / N34 or manual_amount = EF 実際請求。
  {
    headerKey: 'reports.col.p1IssuedTotal',
    cell: (r) => dash(r.p1?.l34),
    csv: (r) => r.p1?.l34 ?? '',
  },
  {
    headerKey: 'reports.col.p1Reference50',
    cell: (r) => dash(r.p1?.m34),
    csv: (r) => r.p1?.m34 ?? '',
  },
  {
    headerKey: 'reports.col.p1ActualRequest',
    cell: (r) => dash(r.p1?.manual_amount ?? r.p1?.n34),
    csv: (r) => r.p1?.manual_amount ?? r.p1?.n34 ?? '',
  },
  {
    headerKey: 'reports.col.p1Status',
    cell: (r) => (r.p1 ? <Badge variant="secondary">{r.p1.status}</Badge> : '—'),
    csv: (r) => phaseStatus(r.p1),
  },
  // P2：⑤T33 税抜総額 / T37 利益分配 / T38 or manual_amount = EF 実際請求。
  {
    headerKey: 'reports.col.p2PreTaxTotal',
    cell: (r) => dash(r.p2?.t33),
    csv: (r) => r.p2?.t33 ?? '',
  },
  {
    headerKey: 'reports.col.p2ProfitShare',
    cell: (r) => dash(r.p2?.t37),
    csv: (r) => r.p2?.t37 ?? '',
  },
  {
    headerKey: 'reports.col.p2ActualRequest',
    cell: (r) => dash(r.p2?.manual_amount ?? r.p2?.t38),
    csv: (r) => r.p2?.manual_amount ?? r.p2?.t38 ?? '',
  },
  {
    headerKey: 'reports.col.p2Status',
    cell: (r) => (r.p2 ? <Badge variant="secondary">{r.p2.status}</Badge> : '—'),
    csv: (r) => phaseStatus(r.p2),
  },
  // P3：三回目（Q2 sign-off 前多為 null）。
  {
    headerKey: 'reports.col.p3Status',
    cell: (r) => (r.p3 ? <Badge variant="secondary">{r.p3.status}</Badge> : '—'),
    csv: (r) => phaseStatus(r.p3),
  },
]

const AUCTION_COLUMNS: readonly ReportColumn<AuctionSummaryRow>[] = [
  { headerKey: 'reports.col.caseId', cell: (r) => r.case_id, csv: (r) => r.case_id },
  { headerKey: 'reports.col.memberOrg', cell: (r) => r.member_org_id, csv: (r) => r.member_org_id },
  { headerKey: 'reports.col.venueOrg', cell: (r) => r.venue_org_id, csv: (r) => r.venue_org_id },
  {
    headerKey: 'reports.col.biddersCount',
    cell: (r) => r.bidders_count,
    csv: (r) => r.bidders_count,
  },
  { headerKey: 'reports.col.bidsCount', cell: (r) => r.bids_count, csv: (r) => r.bids_count },
  { headerKey: 'reports.col.awardsCount', cell: (r) => r.awards_count, csv: (r) => r.awards_count },
  {
    headerKey: 'reports.col.awardedUnitPrice',
    cell: (r) => dash(r.awarded?.auction_unit_price),
    csv: (r) => r.awarded?.auction_unit_price ?? '',
  },
  {
    headerKey: 'reports.col.awardedConfirmedAt',
    cell: (r) => fmtDateTime(r.awarded?.confirmed_at),
    csv: (r) => r.awarded?.confirmed_at ?? '',
  },
]

const FREEE_SLA_COLUMNS: readonly ReportColumn<FreeeSlaRow>[] = [
  { headerKey: 'reports.col.taskId', cell: (r) => r.task_id, csv: (r) => r.task_id },
  { headerKey: 'reports.col.caseId', cell: (r) => r.case_id, csv: (r) => r.case_id },
  {
    headerKey: 'reports.col.settlementPhase',
    cell: (r) => r.settlement_phase,
    csv: (r) => r.settlement_phase,
  },
  { headerKey: 'reports.col.taskType', cell: (r) => r.task_type, csv: (r) => r.task_type },
  {
    headerKey: 'reports.col.status',
    cell: (r) => <Badge variant="outline">{r.status}</Badge>,
    csv: (r) => r.status,
  },
  {
    headerKey: 'reports.col.issuedAt',
    cell: (r) => fmtDateTime(r.issued_at),
    csv: (r) => r.issued_at,
  },
  {
    headerKey: 'reports.col.warningAt',
    cell: (r) => fmtDateTime(r.warning_at),
    csv: (r) => r.warning_at,
  },
  { headerKey: 'reports.col.dueAt', cell: (r) => fmtDateTime(r.due_at), csv: (r) => r.due_at },
  {
    headerKey: 'reports.col.completedAt',
    cell: (r) => fmtDateTime(r.completed_at),
    csv: (r) => r.completed_at ?? '',
  },
]

const AUDIT_COLUMNS: readonly ReportColumn<AuditRow>[] = [
  { headerKey: 'reports.col.auditId', cell: (r) => r.id, csv: (r) => r.id },
  {
    headerKey: 'reports.col.actorUser',
    cell: (r) => dash(r.actor_user_id),
    csv: (r) => r.actor_user_id ?? '',
  },
  {
    headerKey: 'reports.col.organization',
    cell: (r) => dash(r.organization_id),
    csv: (r) => r.organization_id ?? '',
  },
  { headerKey: 'reports.col.eventName', cell: (r) => r.event_name, csv: (r) => r.event_name },
  {
    headerKey: 'reports.col.resourceType',
    cell: (r) => r.resource_type,
    csv: (r) => r.resource_type,
  },
  {
    headerKey: 'reports.col.resourceId',
    cell: (r) => dash(r.resource_id),
    csv: (r) => r.resource_id ?? '',
  },
  {
    headerKey: 'reports.col.beforeSummary',
    cell: (r) => summaryText(r.before_summary),
    csv: (r) => summaryText(r.before_summary),
  },
  {
    headerKey: 'reports.col.afterSummary',
    cell: (r) => summaryText(r.after_summary),
    csv: (r) => summaryText(r.after_summary),
  },
  {
    headerKey: 'reports.col.createdAt',
    cell: (r) => fmtDateTime(r.created_at),
    csv: (r) => r.created_at,
  },
]
